import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import { ESelectedEntityType, type SelectedEntity } from '@common/types/layout.type';

import { arrayLengthEq } from '@common/helpers';

import { Widgets } from '@common/enums';

import type { GridEntity } from '@sites/entity-core';
import type {
    NormalizedColumnEntity,
    NormalizedTabEntity,
    NormalizedSectionEntity,
    WebLegacyWidgetEntity,
} from '@web/types';

import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';

import { NavigationWidgetItemComponent } from '../navigation-widget-item';
import { NavigationGridItemComponent } from '../navigation-grid-item';
import { NavigationTabItemComponent } from '../navigation-tab-item';

import { NavigationDrawerService } from '../navigation-drawer.service';

@Component({
    selector: 'web-navigation-column-item',
    templateUrl: './navigation-column-item.component.html',
    styleUrls: ['./navigation-column-item.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        BsDropdownModule,
        NavigationWidgetItemComponent,
        NavigationGridItemComponent,
        NavigationTabItemComponent,
    ],
})
export class NavigationColumnItemComponent {
    public readonly parentEntity = input.required<NormalizedSectionEntity>();
    public readonly parentIndex = input.required<number>();
    public readonly entity = input.required<NormalizedColumnEntity>();
    public readonly selectedEntity = input.required<SelectedEntity>();
    public readonly isSpecific = input(false);

    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly sectionsFacade: SectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    private readonly gridsFacade: GridsFacade = inject(GridsFacade);
    private readonly tabsFacade: TabsFacade = inject(TabsFacade);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);
    private readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    public readonly showChildren = computed(() => this.navDrawerService.hasColumnId(this.entity().id));

    private readonly entity$ = toObservable(this.entity);
    private readonly widgetsEntities = toSignal(this.widgetsFacade.widgetsEntities$, { initialValue: {} });
    private readonly gridsEntities = toSignal(this.gridsFacade.gridsEntities$, { initialValue: {} });
    private readonly tabsEntities = toSignal(this.tabsFacade.tabsEntities$, { initialValue: {} });

    private readonly hasCartWidget = computed(() => {
        const widgets = this.widgetsEntities();
        return this.entity().children.some(({ id }) => widgets[id]?.name === Widgets.SHOP_CART);
    });

    public readonly isDeleteDisabled = computed(
        () => arrayLengthEq<string>(this.parentEntity().children, 1) || this.hasCartWidget()
    );
    public readonly isCopyDisabled = computed(() => this.hasCartWidget());

    public readonly isActive = computed(() => this.selectedEntity()?.entityId === this.entity().id);

    public readonly index = computed(() =>
        this.parentEntity().children.findIndex((columnId) => columnId === this.entity().id)
    );

    public readonly columnName = computed(() => {
        if (!this.isSpecific()) {
            return 'control_column';
        }

        if (this.index() === 0) {
            return 'control_left_column';
        }

        if (this.index() === 1) {
            return 'control_central_column';
        }

        if (this.index() === 2) {
            return 'control_right_column';
        }

        return 'control_column';
    });

    public readonly displayId = computed(() => 'column-' + this.entity().id);

    /** The column's direct children, resolved from the widget / grid / tab stores. */
    public readonly widgets = computed(() => {
        const widgets = this.widgetsEntities();
        const grids = this.gridsEntities();
        const tabs = this.tabsEntities();

        return this.entity().children.map(({ id }) => widgets[id] ?? grids[id] ?? tabs[id]);
    });

    public readonly isColumnRestricted = toSignal(
        this.entity$.pipe(switchMap((entity) => this.productRestrictionFacade.columnRestricted$(entity))),
        { initialValue: false }
    );

    public selectEntity(): void {
        if (this.navDrawerService.hasColumnId(this.entity().id)) {
            this.navDrawerService.removeColumnId(this.entity().id);
        } else {
            this.navDrawerService.addColumnId(this.entity().id);
        }

        if (this.isSpecific() || this.isActive()) {
            return;
        }

        this.layoutFacade.setSelectedEntityWithScroll({
            entityId: this.entity().id,
            entityType: ESelectedEntityType.column,
        });
    }

    public async removeColumn(): Promise<void> {
        if (this.isDeleteDisabled() || this.isColumnRestricted()) {
            return;
        }

        // The last column of a section cannot stand alone — remove the whole section instead.
        if (this.parentEntity().children.length === 1) {
            const section = await firstValueFrom(this.sectionsFacade.denormalizedSectionById$(this.parentEntity().id));

            if (!section) {
                console.error('Section not found with id', this.parentEntity().id);
                return;
            }

            if (this.isActive()) {
                this.layoutFacade.unsetEntity();
            }

            this.sectionsFacade.removeSection(section, this.parentIndex());
            return;
        }

        if (this.index() === -1) {
            console.error('Column not found in parent entity');
            return;
        }

        const column = await firstValueFrom(this.columnsFacade.denormalizedColumnById$(this.entity().id));

        if (!column) {
            console.error('Column not found with id', this.entity().id);
            return;
        }

        if (this.isActive()) {
            this.layoutFacade.unsetEntity();
        }

        this.columnsFacade.removeColumn(column, this.index());
    }

    public async copyColumn(): Promise<void> {
        if (this.isCopyDisabled() || this.isColumnRestricted()) {
            return;
        }

        if (this.index() === -1) {
            console.error('Column not found in parent entity');
            return;
        }

        const column = await firstValueFrom(this.columnsFacade.denormalizedColumnById$(this.entity().id));

        if (!column) {
            console.error('Column not found with id', this.entity().id);
            return;
        }

        this.columnsFacade.copyColumn(column, this.index());
    }

    public isGrid(widget: WebLegacyWidgetEntity | GridEntity | NormalizedTabEntity): widget is GridEntity {
        return PageStructureService.isEntityGrid(widget);
    }

    public isTab(widget: WebLegacyWidgetEntity | GridEntity | NormalizedTabEntity): widget is NormalizedTabEntity {
        return PageStructureService.isEntityTab(widget);
    }
}
